import React, { useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import character from '../../assets/images/intake/step4/char.svg';
import i_icon from '../../assets/images/intake/step4/i-icon.svg';
import { useIntakeFormContext } from '../../hooks/use-form-context.hooks';
import { dispatchUpdateIntakeFormLanguageProficiency } from '../../redux/slice/form-intake/form-intake.operations';
import {
  IntakeFormLanguageProficiencyDTO,
  LanguageProficiencyValue,
} from '../../redux/slice/form-intake/form-intake.types';
import { useAppDispatch } from '../../redux/store';
import logger from '../../services/logger';

interface LanguageProficiencyProps {
  step: number;
  onBack: () => void;
}

export const LanguageProficiency: React.FC<LanguageProficiencyProps> = ({ step, onBack }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { languageProficiency } = useIntakeFormContext();
  const [selectedEnglishProficiency, setSelectedEnglishProficiency] = useState(
    languageProficiency?.English ? `${languageProficiency.English}` : '',
  );
  const [selectedSpanishProficiency, setSelectedSpanishProficiency] = useState(
    languageProficiency?.Spanish ? `${languageProficiency.Spanish}` : '',
  );

  const [error, setError] = useState('');

  const handleEnglishProficiencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedEnglishProficiency(event.target.value);
    setError('');
  };

  const handleSpanishProficiencyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSpanishProficiency(event.target.value);
    setError('');
  };

  useEffect(() => {
    window.scrollTo(0, 0); // Reset scroll to the top
  }, []);
  useEffect(() => {
    setSelectedEnglishProficiency(`${languageProficiency?.English ?? ''}`);
    setSelectedSpanishProficiency(`${languageProficiency?.Spanish ?? ''}`);
  }, [languageProficiency]);

  const { handleSubmit } = useForm<IntakeFormLanguageProficiencyDTO>();
  const submitLanguageProficiency: SubmitHandler<IntakeFormLanguageProficiencyDTO> = async () => {
    const dto: IntakeFormLanguageProficiencyDTO = {
      languageProficiency: {
        English: parseInt(selectedEnglishProficiency) as LanguageProficiencyValue,
        Spanish: parseInt(selectedSpanishProficiency) as LanguageProficiencyValue,
      },
    };
    logger.debug('Submitted language proficiency.', dto);
    if (
      `${selectedEnglishProficiency ?? ''}` === '' ||
      `${selectedSpanishProficiency ?? ''}` === ''
    ) {
      setError('Please select an option in each language.');
    } else {
      await dispatch(dispatchUpdateIntakeFormLanguageProficiency(dto));
      navigate(`/pre-employment/intake/step${step + 1}`);
    }
  };
  const backButtonClick = () => {
    onBack();
  };
  return (
    <div>
      <div>
        <p className="text-lg mt-7 md:px-10">
          At Vero Learning, we understand the importance of effective communication in today's
          diverse and interconnected world. Language proficiency unlocks your possibilities. This
          section of our pre- employment screening is designed to help us gauge your language skills
          accurately.
        </p>
      </div>
      <div className="flex items-center mt-9 mb-10 md:px-10">
        <div className="bg-gray-200 p-4 rounded-lg font-semibold mr-5 flex items-center">05</div>
        <div>
          <h4 className="text-2xl">
            Rate Your Level of
            <span className="font-semibold"> Language Proficiency</span>
          </h4>
        </div>
      </div>
      <div className="character absolute top-96 left-[-250px] z-[-1]">
        <img className="" src={character} alt="" />
      </div>
      <form
        className="md:px-10"
        action=""
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={handleSubmit(submitLanguageProficiency)}
      >
        <div className="h-[60px] border rounded-lg flex items-center px-6 mb-3">
          <div className="lang-txt  flex basis-full font-semibold">English</div>
          <div className="rating-btns flex justify-end basis-full">
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedEnglishProficiency === '0'}
                  onChange={handleEnglishProficiencyChange}
                  value="0"
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  name="eng"
                />
                <span className="checkmark">0</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedEnglishProficiency === '1'}
                  value="1"
                  onChange={handleEnglishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  name="eng"
                />
                <span className="checkmark">1</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedEnglishProficiency === '2'}
                  onChange={handleEnglishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="2"
                  name="eng"
                />
                <span className="checkmark">2</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedEnglishProficiency === '3'}
                  onChange={handleEnglishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="3"
                  name="eng"
                />
                <span className="checkmark">3</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedEnglishProficiency === '4'}
                  onChange={handleEnglishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="4"
                  name="eng"
                />
                <span className="checkmark">4</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedEnglishProficiency === '5'}
                  onChange={handleEnglishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="5"
                  name="eng"
                />
                <span className="checkmark">5</span>
              </label>
            </div>
          </div>
        </div>
        <div className="h-[60px] border rounded-lg flex items-center px-6 mb-3">
          <div className="lang-txt  flex basis-full font-semibold">Spanish</div>
          <div className="rating-btns flex justify-end basis-full">
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedSpanishProficiency === '0'}
                  onChange={handleSpanishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="0"
                  name="spn"
                />
                <span className="checkmark">0</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedSpanishProficiency === '1'}
                  onChange={handleSpanishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="1"
                  name="spn"
                />
                <span className="checkmark">1</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedSpanishProficiency === '2'}
                  onChange={handleSpanishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="2"
                  name="spn"
                />
                <span className="checkmark">2</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedSpanishProficiency === '3'}
                  onChange={handleSpanishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="3"
                  name="spn"
                />
                <span className="checkmark">3</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedSpanishProficiency === '4'}
                  onChange={handleSpanishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="4"
                  name="spn"
                />
                <span className="checkmark">4</span>
              </label>
            </div>
            <div className="h-[35px] w-[44px] ml-5">
              <label className="radio-container h-full block relative">
                <input
                  checked={selectedSpanishProficiency === '5'}
                  onChange={handleSpanishProficiencyChange}
                  className="w-full h-full absolute top-0 left-0 opacity-0"
                  type="radio"
                  value="5"
                  name="spn"
                />
                <span className="checkmark">5</span>
              </label>
            </div>
          </div>
        </div>
        {error && <p className="text-red-500">{error}</p>}
        <div className="rating-scale mt-8 mb-4">
          <p className="text-lg font-bold mb-4">Rating Scale</p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <div className="bg-gray-100 flex items-center justify-between rounded-md px-5 py-3">
              <div className="flex">
                <div className="font-bold mr-3 text-sm">0</div>
                <p className="text-sm">No Proficiency</p>
              </div>
              <div>
                <img className="" src={i_icon} alt="" />
              </div>
            </div>
            <div className="bg-gray-100 flex items-center justify-between rounded-md px-5 py-3">
              <div className="flex">
                <div className="font-bold mr-3 text-sm">1</div>
                <p className="text-sm">Elementary Proficiency</p>
              </div>
              <div>
                <img className="" src={i_icon} alt="" />
              </div>
            </div>
            <div className="bg-gray-100 flex items-center justify-between rounded-md px-5 py-3">
              <div className="flex">
                <div className="font-bold mr-3 text-sm">2</div>
                <p className="text-sm">Limited Working Proficiency</p>
              </div>
              <div>
                <img className="" src={i_icon} alt="" />
              </div>
            </div>
            <div className="bg-gray-100 flex items-center justify-between rounded-md px-5 py-3">
              <div className="flex">
                <div className="font-bold mr-3 text-sm">3</div>
                <p className="text-sm">Professional Working Proficiency</p>
              </div>
              <div>
                <img className="" src={i_icon} alt="" />
              </div>
            </div>
            <div className="bg-gray-100 flex items-center justify-between rounded-md px-5 py-3">
              <div className="flex">
                <div className="font-bold mr-3 text-sm">4</div>
                <p className="text-sm">Full Professional Proficiency</p>
              </div>
              <div>
                <img className="" src={i_icon} alt="" />
              </div>
            </div>
            <div className="bg-gray-100 flex items-center justify-between rounded-md px-5 py-3">
              <div className="flex">
                <div className="font-bold mr-3 text-sm">5</div>
                <p className="text-sm">Native / Bilingual Proficiency</p>
              </div>
              <div>
                <img className="" src={i_icon} alt="" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-between mt-8">
          <button
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmit(backButtonClick)}
            className="btn-primary"
          >
            Back
          </button>
          <button className="btn-primary">Next</button>
        </div>
      </form>
    </div>
  );
};
